import { Link } from 'react-router-dom';
import type { UtxoBlockListItem } from '../api/types';
import {
  formatBlockTimeDelta,
  formatNumber,
  formatTimeAgo,
  formatTimestamp,
} from '../util/format';

interface BlockCardProps {
  block: UtxoBlockListItem;
}

export const BlockCard = ({ block }: BlockCardProps) => {
  const blockTimeEmoji =
    block.block_time_delta != null
      ? block.block_time_delta > 300
        ? '🐢'
        : block.block_time_delta > 240
        ? '⏱️'
        : block.block_time_delta < 60
        ? '⚡'
        : ''
      : '';

  let chainlockLabel: string | null = null;
  if (block.chainlock_finality) {
    chainlockLabel = '✓ ChainLock Final';
  } else if (block.chainlock) {
    chainlockLabel = '🔒 ChainLocked';
  }

  const chainlockBadge = chainlockLabel ? (
    <span className="badge badge-chainlock">{chainlockLabel}</span>
  ) : null;

  // Count commitments per miner for display
  const minerCounts = new Map<string, number>();
  if (block.syscoin_commitments) {
    for (const commitment of block.syscoin_commitments) {
      const miner = commitment.miner || 'Unknown';
      minerCounts.set(miner, (minerCounts.get(miner) || 0) + 1);
    }
  }

  // Track which occurrence of each miner we're on
  const minerOccurrence = new Map<string, number>();

  const commitmentBadges = block.syscoin_commitments?.map((commitment) => {
    const miner = commitment.miner || 'Unknown';
    const totalCount = minerCounts.get(miner) || 1;
    const currentOccurrence = (minerOccurrence.get(miner) || 0) + 1;
    minerOccurrence.set(miner, currentOccurrence);

    return (
      <Link
        key={commitment.btc_block_hash}
        to={`/auxpow/${commitment.btc_block_hash}`}
        style={{ textDecoration: 'none' }}
      >
        <span className="badge badge-bitcoin">
          ₿ {miner}{totalCount > 1 ? ` ${currentOccurrence}` : ''}
        </span>
      </Link>
    );
  }) || null;

  let minerBadge: JSX.Element | null = null;
  if (block.auxpow_anchor_present && block.auxpow_btc_block_hash) {
    const anchorName = block.syscoin_commitment_miner ? '' : block.auxpow_miner || '';
    const anchorLabel = anchorName
      ? '₿ ⛏️ ' + anchorName
      : '₿ ⛏️';

    if (!block.syscoin_commitment_miner) {
      minerBadge = (
        <Link
          to={`/auxpow/${block.auxpow_btc_block_hash}`}
          style={{ textDecoration: 'none' }}
        >
          <span className="badge badge-auxpow">{anchorLabel}</span>
        </Link>
      );
    }
  } else if (block.auxpow_miner && block.auxpow_btc_block_hash) {
    minerBadge = (
      <Link
        to={`/auxpow/${block.auxpow_btc_block_hash}`}
        style={{ textDecoration: 'none' }}
      >
        <span className="badge badge-auxpow">⛏️ {block.auxpow_miner}</span>
      </Link>
    );
  }

  return (
    <div className="block-card">
      <div
        className="block-header"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: '8px',
          alignItems: 'baseline',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            alignItems: 'baseline',
            flex: 1,
          }}
        >
          <h3 style={{ margin: 0 }}>
            <Link to={`/utxo/block/${block.hash}`}>UTXO Block #{formatNumber(block.height)}</Link>
          </h3>
          <Link
            to={`/utxo/block/${block.hash}`}
            className="block-hash-link"
            style={{
              fontSize: '0.9em',
              lineHeight: 1.2,
              wordBreak: 'break-all',
              paddingTop: 3,
              paddingBottom: 0,
            }}
          >
            🔗 {block.hash}
          </Link>
        </div>
        <span className="timestamp" title={formatTimestamp(block.time)}>
          {formatTimeAgo(block.time)}
        </span>
      </div>
      <div className="block-body">
        <div className="block-stats" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            {block.block_time_delta != null && (
              <span>
                <strong>Block Time:</strong>{' '}
                {formatBlockTimeDelta(block.block_time_delta)} {blockTimeEmoji}
              </span>
            )}
            <span>
              <strong>Confirmations:</strong> {formatNumber(block.confirmations)}
            </span>
            <span>
              <strong>Transactions:</strong> {formatNumber(block.transactions)}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', justifyContent: 'flex-end' }}>
            {commitmentBadges}
            {minerBadge}
            {chainlockBadge}
          </div>
        </div>
      </div>
    </div>
  );
};
